import * as React from 'react';

import { getConfig, URLS } from '@constants';

import { InviteFriendsDialog, StreamPostDialog } from '@components';

import {
	FRIENDS_INVITATION_EVENTS,
	FRIENDS_PAGE_SIZE,
} from './constants';
import {
	JSAPISupportedMethod,
	JSAPIDialogTemplate,
	AppEventName,
} from './enums';
import { parseAppMessage } from './utils';

import {
	IJSAPIProps,
	IJSAPIState,
	IPaymentAppMessage,
	IJSAPIDialogAppMessage,
	JSAPIEventResult,
	IJSAPIWriteHashMessage,
	IFriendsInviteDialogState,
} from './types';

export class JSAPI extends React.PureComponent<IJSAPIProps, IJSAPIState> {

	state: IJSAPIState = {
		activeDialog: null,
	};

	componentDidMount() {
		window.addEventListener('message', this.hanldeMessage.bind(this));
	}

	componentWillUnmount() {
		window.removeEventListener('message', this.hanldeMessage.bind(this));
	}

	sendEventToApp = (eventName: AppEventName, result: JSAPIEventResult) =>
		document.querySelector('iframe').contentWindow.postMessage(
			`event=${eventName}&result=${encodeURIComponent(JSON.stringify(result))}`,
			'*',
		);

	async hanldeMessage({ origin, data }: MessageEvent) {
		if (this.props.appURL.includes(origin)) {
			const message = parseAppMessage(data);

			if (message) {
				switch (message.method) {
					case JSAPISupportedMethod.PaymentDialog:
						return this.showPaymentDialog(message as IPaymentAppMessage);
					case JSAPISupportedMethod.JSAPIDialog:
						return this.showJSAPIDialog(message as IJSAPIDialogAppMessage);
					case JSAPISupportedMethod.ReadHash:
						return this.readHash();
					case JSAPISupportedMethod.WriteHash:
						return this.writeHash((message as IJSAPIWriteHashMessage).hash);
				}
			}
		}
	}

	readHash = () =>
		this.sendEventToApp(AppEventName.ReadHash, {
			hash: document.location.hash.substr(1),
		});

	writeHash(hash: string) {
		document.location.hash = decodeURIComponent(hash);
	}

	async showJSAPIDialog(message: IJSAPIDialogAppMessage) {
		switch (message.template) {
			case JSAPIDialogTemplate.FriendsInvitation:
				return this.showInviteFriendsDialog(message);
			case JSAPIDialogTemplate.StreamPublish:
				return this.showStreamPostDialog(message);
		}
	}

	showPaymentDialog(message: IPaymentAppMessage) {
		const queryParams = {
			/* eslint-disable @typescript-eslint/naming-convention */
			appid: message.app_id,
			session_key: message.session_key,
			service_id: message.service_id,
			service_name: message.service_name,
			mailiki_price: message.mailiki_price,
			mob: 1,
			host: (getConfig().isTestServer && getConfig().isOpenStack && window.location.host.includes('mail.ru'))
				? window.location.host
				: '',
			mobile_spec: getConfig().isAndroidApp ? 'android' : '',
			/* eslint-enable @typescript-eslint/naming-convention */
		};
		const query = Object
			.entries(queryParams)
			.map((pair) => pair.join('='))
			.join('&');
		const paymentUrl = getConfig().isOpenStack
			? `${URLS.PAYMENTM_ABS}?${query}`
			: `${getConfig().hosts.baseUrl}${URLS.PAYMENT_M}?${query}`;

		window.open(paymentUrl, getConfig().isAndroidApp ? '_self' : '_blank').focus();
	}

	hideJSAPIDialog = () => this.setState({ activeDialog: null });

	async showInviteFriendsDialog(message: IJSAPIDialogAppMessage) {
		const { notAppFriends } = this.props;

		FRIENDS_INVITATION_EVENTS
			.forEach((event) => this.sendEventToApp(event, {
				status: 'opened',
			}));

		this.setState({
			activeDialog: {
				appId: message.app_id,
				sessionKey: message.session_key,
				type: JSAPIDialogTemplate.FriendsInvitation,
				friends: notAppFriends || [],
				offset: 0,
				onInvite: this.inviteFriends(message),
				onMore: this.loadMoreFriends.bind(this),
				loading: notAppFriends.length === 0,
			},
		});

		if (notAppFriends.length === 0) {
			await this.loadNextFriendsPage();
		}
	}

	async loadNextFriendsPage() {
		const {
			fetchNotAppFriends,
			currentUser: { auid: uid },
			privateKey,
		} = this.props;
		const { appId, sessionKey, offset, friends } = this.state.activeDialog as IFriendsInviteDialogState;
		const nextPage = await fetchNotAppFriends({
			/* eslint-disable @typescript-eslint/naming-convention */
			app_id: appId,
			session_key: sessionKey,
			uid,
			limit: FRIENDS_PAGE_SIZE,
			offset,
			/* eslint-enable @typescript-eslint/naming-convention */
		}, privateKey);

		this.setState(({ activeDialog }: IJSAPIState) => ({
			activeDialog: {
				...activeDialog,
				loading: false,
				offset: nextPage.length === FRIENDS_PAGE_SIZE
					? offset + FRIENDS_PAGE_SIZE
					: -1, // end
				friends: [...friends, ...nextPage],
			},
		}));
	}

	loadMoreFriends = async () => {
		const { loading, offset } = this.state.activeDialog as IFriendsInviteDialogState;

		if (!loading && offset !== -1) {
			this.setState(({ activeDialog }) => ({
				activeDialog: {
					...activeDialog,
					loading: true,
				},
			}));
			await this.loadNextFriendsPage();
		}
	};

	inviteFriends = (message: IJSAPIDialogAppMessage) =>
		async (uids: string[]) => {
			const {
				inviteNotAppFriends,
				privateKey,
				currentUser: { auid: uid, magicNumber },
			} = this.props;
			const { a: mna, b: mnb } = magicNumber;
			const res = await inviteNotAppFriends({
				/* eslint-disable @typescript-eslint/naming-convention */
				app_id: message.app_id,
				session_key: message.session_key,
				uid,
				mna,
				mnb,
				uids,
				/* eslint-enable @typescript-eslint/naming-convention */
			}, privateKey);

			FRIENDS_INVITATION_EVENTS
				.forEach((event) => this.sendEventToApp(event, {
					status: 'closed',
					data: res ? uids : [],
				}));
			this.hideJSAPIDialog();
		};

	showStreamPostDialog(message: IJSAPIDialogAppMessage) {
		this.sendEventToApp(AppEventName.CommonStreamPublish, {
			status: 'opened',
		});
		this.setState({
			activeDialog: {
				/* eslint-disable @typescript-eslint/naming-convention */
				appName: this.props.app.name,
				type: JSAPIDialogTemplate.StreamPublish,
				title: decodeURIComponent(message.title),
				description: decodeURIComponent(message.text),
				imageUrl: message.img_url,
				onPost: this.postToStream(message),
				/* eslint-enable @typescript-eslint/naming-convention */
			},
		});
	}

	postToStream = (message: IJSAPIDialogAppMessage) =>
		async (text: string) => {
			const { currentUser, makeStreamPost, privateKey } = this.props;

			await makeStreamPost({
				/* eslint-disable @typescript-eslint/naming-convention */
				app_id: message.app_id,
				session_key: message.session_key,
				img_url: message.img_url,
				title: message.title,
				vid: message.vid,
				uid: currentUser.auid,
				text,
				/* eslint-enable @typescript-eslint/naming-convention */
			}, privateKey);
			this.sendEventToApp(AppEventName.CommonStreamPublish, {
				status: 'publishSuccess',
			});
			this.hideJSAPIDialog();
		};

	render(): JSX.Element {
		const { app } = this.props;
		const { activeDialog } = this.state;

		return <>
			{activeDialog?.type === JSAPIDialogTemplate.FriendsInvitation &&
				<InviteFriendsDialog
					appTitle={app.name}
					friends={activeDialog.friends}
					onInvite={activeDialog.onInvite}
					onMore={activeDialog.onMore}
					onClose={this.hideJSAPIDialog}
					loading={activeDialog.loading}
				/>}

			{activeDialog?.type === JSAPIDialogTemplate.StreamPublish &&
				<StreamPostDialog
					appName={activeDialog.appName}
					title={activeDialog.title}
					description={activeDialog.description}
					imageUrl={activeDialog.imageUrl}
					onPublish={activeDialog.onPost}
					onClose={this.hideJSAPIDialog}
				/>}
		</>;
	}

}
